import React, { useEffect, useRef } from 'react';
import { noise, trace, Grid } from '../../components/figures/contours';
import { readInk } from '../../components/figures/CanvasFigure';

// The hero's contour map, with a signal gliding over it. The map is what the model knows;
// the signal is something that happened in production. Around it the ground settles into
// a valley and the lines warm to amber as the map adapts, then relax once the signal moves
// on. Before anyone points, the signal drifts on its own; once a pointer arrives, it leads.

const CELL = 8;
const LEVELS = [-0.5, -0.4, -0.3, -0.2, -0.1, 0, 0.1, 0.2, 0.3, 0.4, 0.5];
// Reach of the signal in pixels; how fast the map adapts under it; how many seconds it
// takes to relax; how far adapted ground settles, in contour levels.
const REACH = 110;
const ADAPT = 6;
const RELAX = 0.45;
const SETTLE = 0.09;
// Adapted segments are stroked in this many even steps, so the tint warms smoothly.
const TINT_STEPS = 12;
// The terrain drifts by under a pixel in this many seconds, so it is refreshed this often.
const TERRAIN_EVERY = 0.25;

const base = noise(2);
const detail = noise(4);
const terrainAt = (x: number, y: number, t: number) => base(x / 170 + t * 0.004, y / 130) + 0.32 * detail(x / 75, y / 75);

const clamp = (x: number, a: number, b: number) => Math.min(b, Math.max(a, x));

// Where the signal drifts before anyone points: a slow loop through the middle of the map,
// which sits beside the words or under them, never behind them.
function drift(w: number, h: number, t: number): [number, number] {
  return [
    w * (0.6 + 0.12 * Math.sin(t * 0.11) + 0.05 * Math.sin(t * 0.29 + 1.3)),
    h * (0.5 + 0.1 * Math.sin(t * 0.083 + 2.1) + 0.04 * Math.sin(t * 0.21 + 0.4)),
  ];
}

interface Signal { x: number; y: number; vx: number; vy: number; seen: number }
interface Pointer { x: number; y: number; inside: boolean }

export function ContourField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return undefined;

    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
    let ink = readInk();
    let w = 0;
    let h = 0;
    let t = 3;
    let raf = 0;
    let last = 0;
    let visible = false;
    let grid: Grid = { cols: 0, rows: 0, cell: CELL, values: new Float32Array(0) };
    let terrain = new Float32Array(0);
    let adapted = new Float32Array(0);
    let terrainTime = -Infinity;
    let signal: Signal = { x: 0, y: 0, vx: 0, vy: 0, seen: 1 };
    let pointer: Pointer | null = null;
    let pointed = false;

    // The map adapts under the signal, most at its centre, and relaxes everywhere else.
    const adapt = (x: number, y: number, strength: number, dt: number) => {
      const keep = Math.exp(-dt / RELAX);
      for (let k = 0; k < adapted.length; k++) adapted[k] *= keep;
      if (strength <= 0) return;
      const { cols, rows } = grid;
      const i0 = Math.max(0, Math.floor((x - REACH) / CELL));
      const i1 = Math.min(cols - 1, Math.ceil((x + REACH) / CELL));
      const j0 = Math.max(0, Math.floor((y - REACH) / CELL));
      const j1 = Math.min(rows - 1, Math.ceil((y + REACH) / CELL));
      for (let j = j0; j <= j1; j++) {
        for (let i = i0; i <= i1; i++) {
          const d = Math.hypot(i * CELL - x, j * CELL - y);
          if (d >= REACH) continue;
          const q = 1 - d / REACH;
          const k = j * cols + i;
          adapted[k] += ADAPT * strength * dt * q * q * (3 - 2 * q) * (1 - adapted[k]);
        }
      }
    };

    // A critically damped glide toward the target: no overshoot, no snapping.
    const glide = (tx: number, ty: number, stiffness: number, dt: number) => {
      const damping = 2 * Math.sqrt(stiffness);
      const n = Math.ceil(dt / 0.008);
      for (let i = 0; i < n; i++) {
        const step = dt / n;
        signal.vx += (stiffness * (tx - signal.x) - damping * signal.vx) * step;
        signal.vy += (stiffness * (ty - signal.y) - damping * signal.vy) * step;
        signal.x += signal.vx * step;
        signal.y += signal.vy * step;
      }
    };

    const step = (dt: number) => {
      const over = !!pointer && pointer.inside;
      if (over) pointed = true;
      const showing = over || !pointed;
      const [tx, ty] = over && pointer ? [pointer.x, pointer.y] : pointed ? [signal.x, signal.y] : drift(w, h, t);
      if (over && pointer && signal.seen < 0.05) Object.assign(signal, { x: pointer.x, y: pointer.y, vx: 0, vy: 0 });
      glide(tx, ty, over ? 140 : 4, dt);
      signal.seen = clamp(signal.seen + (showing ? dt : -dt) / 0.35, 0, 1);
      adapt(signal.x, signal.y, signal.seen, dt);
    };

    const draw = () => {
      if (!w || !h) return;
      const { cols, rows } = grid;
      if (t - terrainTime > TERRAIN_EVERY || terrainTime > t) {
        for (let j = 0; j < rows; j++) for (let i = 0; i < cols; i++) terrain[j * cols + i] = terrainAt(i * CELL, j * CELL, t);
        terrainTime = t;
      }
      // Adapted ground sits a little lower, so the contours bend toward it.
      for (let k = 0; k < grid.values.length; k++) grid.values[k] = terrain[k] - SETTLE * adapted[k];

      ctx.clearRect(0, 0, w, h);
      ctx.globalAlpha = 1;
      LEVELS.forEach((level) => {
        const plain = new Path2D();
        const tinted = Array.from({ length: TINT_STEPS }, () => new Path2D());
        const used = new Array<boolean>(TINT_STEPS).fill(false);
        trace(grid, level, (x1, y1, x2, y2, k) => {
          plain.moveTo(x1, y1);
          plain.lineTo(x2, y2);
          const m = (adapted[k] + adapted[k + 1] + adapted[k + cols] + adapted[k + cols + 1]) / 4;
          if (m <= 0.02) return;
          const s = Math.min(TINT_STEPS - 1, Math.floor(m * TINT_STEPS));
          used[s] = true;
          tinted[s].moveTo(x1, y1);
          tinted[s].lineTo(x2, y2);
        });
        ctx.strokeStyle = ink.line(0.13);
        ctx.lineWidth = 0.8;
        ctx.stroke(plain);
        tinted.forEach((shape, s) => {
          if (!used[s]) return;
          ctx.globalAlpha = 1 - Math.pow(1 - (s + 0.5) / TINT_STEPS, 2.4);
          ctx.strokeStyle = ink.amber;
          ctx.lineWidth = 1.2 + 0.8 * (s / TINT_STEPS);
          ctx.stroke(shape);
        });
        ctx.globalAlpha = 1;
      });

      // The signal: a fine ring around a point.
      if (signal.seen > 0) {
        ctx.globalAlpha = signal.seen;
        ctx.strokeStyle = ink.line(0.9);
        ctx.lineWidth = 1.1;
        ctx.beginPath();
        ctx.arc(signal.x, signal.y, 10, 0, Math.PI * 2);
        ctx.stroke();
        ctx.fillStyle = ink.line(0.9);
        ctx.beginPath();
        ctx.arc(signal.x, signal.y, 2.4, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    };

    const running = () => visible && !document.hidden && !reduce.matches;

    const frame = (now: number) => {
      const dt = clamp((now - last) / 1000, 0, 0.1);
      last = now;
      t += dt;
      step(dt);
      draw();
      raf = running() ? requestAnimationFrame(frame) : 0;
    };

    const kick = () => {
      if (running()) {
        if (!raf) {
          last = performance.now();
          raf = requestAnimationFrame(frame);
        }
      } else {
        cancelAnimationFrame(raf);
        raf = 0;
        draw();
      }
    };

    // Sizes the backing store from the client box, and starts the map afresh at that size.
    const resize = () => {
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      w = canvas.clientWidth;
      h = canvas.clientHeight;
      canvas.width = Math.round(w * dpr);
      canvas.height = Math.round(h * dpr);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const cols = Math.ceil(w / CELL) + 1;
      const rows = Math.ceil(h / CELL) + 1;
      grid = { cols, rows, cell: CELL, values: new Float32Array(cols * rows) };
      terrain = new Float32Array(cols * rows);
      adapted = new Float32Array(cols * rows);
      terrainTime = -Infinity;
      const [x, y] = drift(w, h, t);
      signal = { x, y, vx: 0, vy: 0, seen: 1 };
      // Let the drift run for a moment first, so the first frame already shows the idea.
      for (let u = t - 1.5; u < t; u += 0.05) {
        const [px, py] = drift(w, h, u);
        adapt(px, py, 1, 0.05);
      }
      draw();
    };

    const follow = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      const x = e.clientX - r.left;
      const y = e.clientY - r.top;
      const inside = x >= 0 && y >= 0 && x <= r.width && y <= r.height;
      pointer = inside ? { x, y, inside } : pointer && { ...pointer, inside: false };
    };
    const leave = () => { if (pointer) pointer = { ...pointer, inside: false }; };
    const repaint = () => { ink = readInk(); draw(); };

    const sizer = new ResizeObserver(resize);
    sizer.observe(canvas);
    const seen = new IntersectionObserver(([entry]) => { visible = entry.isIntersecting; kick(); });
    seen.observe(canvas);
    const themeWatch = new MutationObserver(repaint);
    themeWatch.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    window.addEventListener('pointermove', follow, { passive: true });
    document.documentElement.addEventListener('mouseleave', leave);
    window.addEventListener('blur', leave);
    document.addEventListener('visibilitychange', kick);
    reduce.addEventListener?.('change', kick);

    return () => {
      cancelAnimationFrame(raf);
      sizer.disconnect();
      seen.disconnect();
      themeWatch.disconnect();
      window.removeEventListener('pointermove', follow);
      document.documentElement.removeEventListener('mouseleave', leave);
      window.removeEventListener('blur', leave);
      document.removeEventListener('visibilitychange', kick);
      reduce.removeEventListener?.('change', kick);
    };
  }, []);

  // Out of flow, so resizing its backing store never changes the box the observer watches.
  return <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />;
}

export default ContourField;
